import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Nav from './components/Nav';
import HowItWorks from './pages/HowItWorks';
import Assessment from './pages/Assessment';
import HowItWasBuilt from './pages/HowItWasBuilt';
import Science from './pages/Science';
import Results from './pages/Results';
import Coach from './pages/Coach';
import NotFound from './pages/NotFound';

// ── Session state shape ──────────────────────────────────────────────────────
// assessmentAnswers: raw answers object from the assessment form
// protocol:         generated protocol object { foundational, modules, profile }
// lifestyleGuidance: generated lifestyle guidance report string
// Both protocol and lifestyleGuidance are null until the assessment is submitted.
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  const [assessmentAnswers, setAssessmentAnswers] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [lifestyleGuidance, setLifestyleGuidance] = useState(null);
  const [retakeKey, setRetakeKey] = useState(0);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('lih_session');
      if (saved) {
        const { answers, protocol: p, guidance } = JSON.parse(saved);
        if (answers && p) {
          setAssessmentAnswers(answers);
          setProtocol(p);
          setLifestyleGuidance(guidance || null);
        }
      }
    } catch (e) { /* ignore parse/storage errors */ }
  }, []);

  // Called by Assessment page on submit — fires both engines simultaneously
  function handleAssessmentComplete(answers, generatedProtocol, generatedGuidance) {
    setAssessmentAnswers(answers);
    setProtocol(generatedProtocol);
    setLifestyleGuidance(generatedGuidance);
    // Persist session so protocol survives page refresh
    try {
      localStorage.setItem('lih_session', JSON.stringify({
        answers,
        protocol: generatedProtocol,
        guidance: generatedGuidance,
      }));
    } catch (e) { /* ignore storage errors */ }
  }

  // Clear session — called by Retake Assessment
  function handleRetake() {
    try { localStorage.removeItem('lih_session'); } catch {}
    setAssessmentAnswers(null);
    setProtocol(null);
    setLifestyleGuidance(null);
    setRetakeKey(k => k + 1);
  }

  // Guard: Results and Coach require a completed assessment
  function RequireAssessment({ children }) {
    if (!protocol) return <Navigate to="/assessment" replace />;
    return children;
  }

  return (
    <BrowserRouter>
      <Nav hasResults={!!protocol} onRetake={handleRetake} />
      <Routes>
        <Route path="/" element={<HowItWorks />} />
        <Route
          path="/assessment"
          element={
            <Assessment
              key={retakeKey}
              onComplete={handleAssessmentComplete}
              existingAnswers={assessmentAnswers}
            />
          }
        />
        <Route
          path="/results"
          element={
            <RequireAssessment>
              <Results
                protocol={protocol}
                lifestyleGuidance={lifestyleGuidance}
                assessmentAnswers={assessmentAnswers}
              />
            </RequireAssessment>
          }
        />
        <Route
          path="/coach"
          element={
            <RequireAssessment>
              <Coach
                protocol={protocol}
                lifestyleGuidance={lifestyleGuidance}
                assessmentAnswers={assessmentAnswers}
              />
            </RequireAssessment>
          }
        />
        <Route path="/how-it-was-built" element={<HowItWasBuilt />} />
        <Route path="/science" element={<Science />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
